import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { user_Signup } from "../../component/services/UserApiServices/UserApiCalls";
import { AppStoreContext } from "../../store/AppStore";
import {
  isBlockedEmailDomain,
  PROFESSIONAL_EMAIL_MSG,
} from "../utils/blockedEmailDomains";
import TurnstileWidget from "../components/TurnstileWidget";
import "../sass/MottruckLanding.scss";
import "./ContactDemo.scss";

const COUNTRIES = [
  { name: "France", code: "+33" },
  { name: "Belgium", code: "+32" },
  { name: "Netherlands", code: "+31" },
  { name: "Germany", code: "+49" },
  { name: "Spain", code: "+34" },
  { name: "Italy", code: "+39" },
  { name: "Poland", code: "+48" },
  { name: "Portugal", code: "+351" },
  { name: "Romania", code: "+40" },
  { name: "United Kingdom", code: "+44" },
];

const PWD_RULES = [
  { key: "len", label: "8 characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "1 capital letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "1 lowercase", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "1 number", test: (p) => /[0-9]/.test(p) },
  {
    key: "special",
    label: "1 special",
    test: (p) => /[!@#$%^&*]/.test(p),
  },
];

const STRONG_PWD =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;

const Join = () => {
  const navigate = useNavigate();
  const { setAccess, setUserDetails, setIsLoading } = useContext(AppStoreContext);

  const [role, setRole] = useState(1); // 0 shipper, 1 carrier
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("France");
  const [email, setEmail] = useState("");
  const [phoneCode, setPhoneCode] = useState("+33");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileReset, setTurnstileReset] = useState(0);

  const pwdChecks = useMemo(
    () => PWD_RULES.map((r) => ({ ...r, ok: r.test(password) })),
    [password]
  );

  const validate = () => {
    const next = {};
    if (!firstName.trim()) next.firstName = "First name is required.";
    if (!lastName.trim()) next.lastName = "Last name is required.";
    if (!company.trim()) next.company = "Company is required.";
    if (!country.trim()) next.country = "Country is required.";
    if (!email.trim()) next.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      next.email = "Please enter a valid email.";
    if (!phone.trim()) next.phone = "Phone is required.";
    if (!password) next.password = "Password is required.";
    else if (!STRONG_PWD.test(password))
      next.password =
        "Password needs 8+ chars, upper, lower, number and special (!@#$%^&*).";
    if (!terms) next.terms = "Please accept the Privacy Policy and Terms.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (isBlockedEmailDomain(email)) {
      toast.error(PROFESSIONAL_EMAIL_MSG);
      return;
    }
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!turnstileToken) {
      toast.error("Please complete the security check.");
      return;
    }

    const profile = role === 0 ? "User" : "Carrier";
    const countryMeta = COUNTRIES.find((c) => c.name === country);
    const payload = {
      profile,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      company: company.trim(),
      phone: phone.replace(/\s+/g, "").trim(),
      email: email.trim().toLowerCase(),
      password,
      country: country,
      CountryCode: phoneCode || countryMeta?.code || "+33",
      countryName: country,
      marketingOptIn: optIn,
      turnstileToken,
    };

    setSubmitting(true);
    setIsLoading?.(true);
    try {
      const res = await user_Signup(payload);
      if (res?.status) {
        const access = profile === "User" ? "userAccess" : "carrierAccess";
        localStorage.setItem("userDetail", JSON.stringify(res.data));
        localStorage.setItem("token", JSON.stringify(res.token));
        localStorage.setItem("access", JSON.stringify(access));
        setAccess?.(access);
        setUserDetails?.(res.data);
        toast.success("Account created successfully");
        navigate("/account", { replace: true });
      } else {
        toast.error(res?.msg || "Could not create account.");
        setTurnstileToken(null);
        setTurnstileReset((n) => n + 1);
      }
    } catch (err) {
      console.error("Join signup error:", err);
      toast.error("Something went wrong. Please try again.");
      setTurnstileToken(null);
      setTurnstileReset((n) => n + 1);
    } finally {
      setSubmitting(false);
      setIsLoading?.(false);
    }
  };

  return (
    <div className="mtk-landing mtk-split-page mtk-join-page">
      <ToastContainer position="top-right" autoClose={4000} />
      <section className="mtk-split-section mtk-join-section">
        <div className="mtk-split-grid mtk-join-grid">
          <div className="mtk-split-left mtk-join-left">
            <h1 className="mtk-join-title">Create your account.</h1>
          </div>

          <div className="mtk-split-right mtk-join-right">
            <form className="mtk-join-form" onSubmit={onSubmit} noValidate>
              <div className="field">
                <label>
                  I am joining as <span className="req">*</span>
                </label>
                <div className="mtk-join-chips">
                  <button
                    type="button"
                    className={role === 0 ? "on" : ""}
                    onClick={() => setRole(0)}
                  >
                    Shipper
                  </button>
                  <button
                    type="button"
                    className={role === 1 ? "on" : ""}
                    onClick={() => setRole(1)}
                  >
                    Carrier
                  </button>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label>
                    First name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                  />
                  {errors.firstName && <p className="field-err">{errors.firstName}</p>}
                </div>
                <div className="field">
                  <label>
                    Last name <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Carter"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                  />
                  {errors.lastName && <p className="field-err">{errors.lastName}</p>}
                </div>
              </div>

              <div className="field-row company-country">
                <div className="field">
                  <label>
                    Company <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Your company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    autoComplete="organization"
                  />
                  {errors.company && <p className="field-err">{errors.company}</p>}
                </div>
                <div className="field">
                  <label>
                    Country <span className="req">*</span>
                  </label>
                  <select
                    value={country}
                    onChange={(e) => {
                      const name = e.target.value;
                      setCountry(name);
                      const meta = COUNTRIES.find((c) => c.name === name);
                      if (meta) setPhoneCode(meta.code);
                    }}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {errors.country && <p className="field-err">{errors.country}</p>}
                </div>
              </div>

              <div className="field-row email-phone">
                <div className="field">
                  <label>
                    Working email <span className="req">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                  {errors.email && <p className="field-err">{errors.email}</p>}
                </div>
                <div className="field">
                  <label>
                    Phone <span className="req">*</span>
                  </label>
                  <div className="phone-row">
                    <select
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value)}
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="6 00 00 00"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      autoComplete="tel-national"
                    />
                  </div>
                  {errors.phone && <p className="field-err">{errors.phone}</p>}
                </div>
              </div>

              <div className="field mtk-join-password">
                <label>
                  Password <span className="req">*</span>
                </label>
                <div className="pwd-wrap">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="pwd-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <div className="mtk-pwd-rules">
                  {pwdChecks.map((r) => (
                    <span key={r.key} className={`rule${r.ok ? " ok" : ""}`}>
                      <span className="dot" />
                      {r.label}
                    </span>
                  ))}
                </div>
                {errors.password && <p className="field-err">{errors.password}</p>}
              </div>

              <div className="mtk-join-checks">
                <label htmlFor="join-optin" className="check mtk-join-optin">
                  <input
                    id="join-optin"
                    type="checkbox"
                    checked={optIn}
                    onChange={(e) => setOptIn(e.target.checked)}
                  />
                  <span>
                    I want to receive personalized commercial communications from Mottruck by email.
                  </span>
                </label>
                <label htmlFor="join-terms" className="check">
                  <input
                    id="join-terms"
                    type="checkbox"
                    checked={terms}
                    onChange={(e) => setTerms(e.target.checked)}
                  />
                  <span>
                    I have read and accept the{" "}
                    <Link to="/privacy">Privacy Policy</Link> and{" "}
                    <Link to="/terms">Terms &amp; Conditions</Link>.{" "}
                    <span className="req">*</span>
                  </span>
                </label>
                {errors.terms && <p className="field-err">{errors.terms}</p>}
              </div>

              <div className="mtk-turnstile-slot">
                <TurnstileWidget
                  onToken={setTurnstileToken}
                  resetKey={turnstileReset}
                />
                <div className="mtk-join-actions">
                  <button
                    type="submit"
                    className="mtk-join-submit"
                    disabled={submitting}
                  >
                    {submitting ? "Creating account…" : "Create my account →"}
                  </button>
                  <div className="mtk-join-login-hint">
                    Already have an account?{" "}
                    <Link to="/user_login">Log in</Link>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Join;
